import { Cloud, AlertCircle, RefreshCw } from 'lucide-react'
import { cn, formatRelativeTime } from '@/lib/utils'
import {
  useCloudflareStatus,
  type CloudflarePagesProject,
  type CloudflareWorkerScript,
  type CloudflareWafTopRule,
} from '@/hooks/useCloudflareStatus'
import { ServiceHealthCard, type ServiceStatus, type SubMetric } from './shared/ServiceHealthCard'
import { RealTimeIndicator } from './shared/RealTimeIndicator'

// WAF threshold bands. Tuned without ground truth: the March 30 incident
// was qualitative-only in the memory, no event count cited. Today's
// baseline is ~20k/24h (normal background bot scanning). Tightening pass
// planned after 4 weeks of baseline data; see issues-queue.md.
const WAF_YELLOW_AT = 50_000
const WAF_RED_AT = 200_000

const MISSING = '—'

function fmtNum(n: number | undefined): string {
  return n === undefined ? MISSING : n.toLocaleString()
}

function fmtMicrosAsMs(n: number | undefined): string {
  return n === undefined ? MISSING : `${(n / 1000).toFixed(2)} ms`
}

function pagesStatus(p: CloudflarePagesProject): ServiceStatus {
  if (!p.ok) return 'error'
  const s = p.latestDeployment?.status
  if (s === 'success' || s === 'idle' || s === 'active') return 'ok'
  if (s === 'failure' || s === 'canceled') return 'error'
  return 'unknown'
}

function pagesStatusLabel(p: CloudflarePagesProject): string {
  if (!p.ok) return 'error'
  return p.latestDeployment?.status ?? 'unknown'
}

function workerStatus(w: CloudflareWorkerScript): ServiceStatus {
  if (!w.ok) return 'error'
  if ((w.errors24h ?? 0) > 0) return 'degraded'
  return 'ok'
}

function workerStatusLabel(w: CloudflareWorkerScript): string {
  if (!w.ok) return 'error'
  if ((w.errors24h ?? 0) > 0) return 'errors'
  return 'ok'
}

function wafStatus(total: number | undefined): ServiceStatus {
  if (total === undefined) return 'unknown'
  if (total > WAF_RED_AT) return 'error'
  if (total > WAF_YELLOW_AT) return 'degraded'
  return 'ok'
}

function wafStatusLabel(total: number | undefined): string {
  if (total === undefined) return 'unknown'
  if (total > WAF_RED_AT) return 'high'
  if (total > WAF_YELLOW_AT) return 'elevated'
  return 'normal'
}

function pagesSubMetrics(p: CloudflarePagesProject): SubMetric[] {
  const dep = p.latestDeployment
  return [
    { label: 'Stage', value: dep?.stage ?? MISSING },
    { label: 'Env', value: dep?.environment ?? MISSING },
    {
      label: 'Deployed',
      value: dep?.createdAt ? `${formatRelativeTime(dep.createdAt)} ago` : MISSING,
      hint: dep?.createdAt,
    },
    { label: 'ID', value: dep ? <span className="font-mono text-xs" title={dep.id}>{dep.id.slice(0, 8)}</span> : MISSING },
  ]
}

function workerSubMetrics(w: CloudflareWorkerScript): SubMetric[] {
  return [
    { label: 'Requests (24h)', value: fmtNum(w.requests24h) },
    { label: 'Errors (24h)', value: fmtNum(w.errors24h) },
    { label: 'CPU p50', value: fmtMicrosAsMs(w.cpuTimeP50) },
    { label: 'CPU p99', value: fmtMicrosAsMs(w.cpuTimeP99) },
  ]
}

const WAF_HINT =
  'Tuned without ground truth (March 30 incident not quantified). ' +
  'Tightening pass planned after 4 weeks of baseline data.'

function WafTopRules({ rules }: { rules: CloudflareWafTopRule[] | undefined }) {
  if (!rules || rules.length === 0) {
    return <p className="text-xs text-muted-foreground">No rule activity in the last 24h.</p>
  }
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Top rules (24h)</p>
      <ul className="space-y-1.5">
        {rules.map((r) => (
          <li
            key={r.ruleId}
            className="flex items-baseline justify-between gap-2 text-xs"
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="font-mono truncate" title={r.ruleId}>
                {r.ruleId.slice(0, 8)}
              </span>
              <span className="text-muted-foreground shrink-0">
                {r.source} / {r.action}
              </span>
            </span>
            <span className="font-medium tabular-nums">{r.count.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SubGroupHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2 mt-4 first:mt-0">
      {children}
    </h4>
  )
}

function SectionShell({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
          <Cloud className="h-5 w-5 text-brand-fg" />
          Cloudflare
        </h3>
        <RealTimeIndicator />
      </div>
      {children}
    </div>
  )
}

export function CloudflareSection() {
  const { data, isLoading, isError, error, refetch, isFetching } = useCloudflareStatus()

  if (isLoading) {
    return (
      <SectionShell>
        <div className="space-y-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <ServiceHealthCard key={i} title="" status="unknown" isLoading />
          ))}
        </div>
      </SectionShell>
    )
  }

  if (isError || !data) {
    const msg = error instanceof Error ? error.message : 'Cloudflare status function is unreachable.'
    return (
      <SectionShell>
        <div className="rounded-lg border border-status-error/30 bg-status-error/10 p-4 sm:p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-status-error shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-status-error font-medium">{msg}</p>
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-status-error/30 text-xs text-status-error hover:bg-status-error/10 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
                Retry
              </button>
            </div>
          </div>
        </div>
      </SectionShell>
    )
  }

  const wafTotal = data.waf.totalEvents24h
  const wafSubMetrics: SubMetric[] = [
    {
      label: 'Events (24h)',
      value: <span className="text-base font-semibold tabular-nums">{fmtNum(wafTotal)}</span>,
      hint: WAF_HINT,
    },
    { label: 'Zone', value: 'dextoro.com' },
  ]

  return (
    <SectionShell>
      <SubGroupHeading>Pages Deployments</SubGroupHeading>
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {data.pages.projects.map((p) => (
          <ServiceHealthCard
            key={p.project}
            title={`Pages: ${p.project}`}
            url={p.latestDeployment?.url}
            status={pagesStatus(p)}
            statusLabel={pagesStatusLabel(p)}
            isError={!p.ok}
            errorMessage={p.error}
            onRetry={!p.ok ? () => refetch() : undefined}
            isRetrying={isFetching}
            subMetrics={p.ok ? pagesSubMetrics(p) : undefined}
          />
        ))}
        {data.pages.projects.length === 0 && (
          <div className="col-span-2 rounded-lg border bg-card p-4 text-sm text-muted-foreground">
            {data.pages.error ?? 'No Pages projects returned.'}
          </div>
        )}
      </div>

      <SubGroupHeading>Workers (24h)</SubGroupHeading>
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {data.workers.scripts.map((w) => (
          <ServiceHealthCard
            key={w.script}
            title={`Worker: ${w.script}`}
            status={workerStatus(w)}
            statusLabel={workerStatusLabel(w)}
            isError={!w.ok}
            errorMessage={w.error}
            onRetry={!w.ok ? () => refetch() : undefined}
            isRetrying={isFetching}
            subMetrics={w.ok ? workerSubMetrics(w) : undefined}
          />
        ))}
        {data.workers.scripts.length === 0 && (
          <div className="col-span-2 rounded-lg border bg-card p-4 text-sm text-muted-foreground">
            {data.workers.error ?? 'No Workers scripts returned.'}
          </div>
        )}
      </div>

      <SubGroupHeading>WAF Events (24h)</SubGroupHeading>
      <ServiceHealthCard
        title="WAF: dextoro.com"
        status={data.waf.ok ? wafStatus(wafTotal) : 'error'}
        statusLabel={data.waf.ok ? wafStatusLabel(wafTotal) : 'error'}
        isError={!data.waf.ok}
        errorMessage={data.waf.error}
        onRetry={!data.waf.ok ? () => refetch() : undefined}
        isRetrying={isFetching}
        subMetrics={data.waf.ok ? wafSubMetrics : undefined}
      >
        {data.waf.ok && <WafTopRules rules={data.waf.topRules} />}
      </ServiceHealthCard>
    </SectionShell>
  )
}
